import React, { useEffect, useState, useMemo } from "react";
import { LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { ChartLineUp } from "@phosphor-icons/react";
import api from "../../lib/api";

const GAMES = [
  { key: "default_simon", label: "Simon", metric: "correct_taps", metricLabel: "Nivel", color: "#32D74B", higherIsBetter: true },
  { key: "default_stroop", label: "Stroop", metric: "avg_reaction_ms", metricLabel: "RT (ms)", color: "#FF9500", higherIsBetter: false },
  { key: "default_go_nogo", label: "Go / No-Go", metric: "avg_reaction_ms", metricLabel: "RT (ms)", color: "#007AFF", higherIsBetter: false },
];

/**
 * Muestra 3 mini-charts (uno por juego rápido) con la evolución del atleta:
 * - Simon: nivel máximo alcanzado por intento (mayor = mejor)
 * - Stroop / Go-NoGo: reaction time promedio por intento (menor = mejor)
 *
 * Si se pasa `userId` (admin viendo a otro atleta), lo filtra en el backend;
 * si no, el backend devuelve los del usuario logueado.
 */
export default function NeuroProgress({ userId = null }) {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const path = userId ? `/neuroscience/results?user_id=${userId}` : "/neuroscience/results";
        const { data } = await api.get(path);
        setResults(data || []);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [userId]);

  const byGame = useMemo(() => {
    const out = {};
    for (const g of GAMES) out[g.key] = [];
    // De más viejo a más nuevo para que el eje X progrese de izquierda a derecha
    const sorted = [...results].sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
    let counters = { default_simon: 0, default_stroop: 0, default_go_nogo: 0 };
    for (const r of sorted) {
      if (!(r.config_id in out)) continue;
      counters[r.config_id] += 1;
      const value = r[GAMES.find((g) => g.key === r.config_id).metric];
      if (value == null) continue;
      out[r.config_id].push({
        attempt: counters[r.config_id],
        value: Number(value),
        date: (r.created_at || "").slice(0, 10),
      });
    }
    return out;
  }, [results]);

  if (loading) {
    return <div className="text-muted text-sm py-6 text-center">Cargando evolución…</div>;
  }

  const anyData = GAMES.some((g) => byGame[g.key].length >= 2);
  if (!anyData) {
    return (
      <div className="card-base p-8 text-center text-muted text-sm" data-testid="neuro-progress-empty">
        Necesitás al menos 2 intentos por juego para ver tu evolución. Jugá un par más y volvé.
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="neuro-progress">
      {GAMES.map((g) => {
        const data = byGame[g.key];
        const best = data.length
          ? (g.higherIsBetter
              ? Math.max(...data.map((d) => d.value))
              : Math.min(...data.map((d) => d.value)))
          : null;
        return (
          <div key={g.key} className="card-base p-4" data-testid={`neuro-progress-${g.key}`}>
            <div className="flex items-center justify-between mb-3 pb-3 border-b border-app">
              <div>
                <div className="font-display font-black uppercase text-sm tracking-tight">{g.label}</div>
                <div className="text-[10px] uppercase tracking-widest text-muted">{g.metricLabel}</div>
              </div>
              {best != null && (
                <div className="text-right">
                  <div className="text-[9px] uppercase tracking-widest text-muted">Mejor</div>
                  <div className="font-display font-black text-lg" style={{ color: g.color }}>
                    {g.metric === "avg_reaction_ms" ? `${Math.round(best)}ms` : best}
                  </div>
                </div>
              )}
            </div>
            {data.length < 2 ? (
              <div className="text-xs text-muted py-6 text-center">Jugá al menos 2 veces para ver la línea.</div>
            ) : (
              <div style={{ width: "100%", height: 160 }}>
                <ResponsiveContainer>
                  <LineChart data={data} margin={{ top: 10, right: 8, left: -12, bottom: 0 }}>
                    <CartesianGrid stroke="#262626" strokeDasharray="3 3" />
                    <XAxis dataKey="attempt" stroke="#8E8E93" tick={{ fontSize: 10 }} />
                    <YAxis stroke="#8E8E93" tick={{ fontSize: 10 }} />
                    <Tooltip
                      contentStyle={{ backgroundColor: "#0A0A0A", border: "1px solid #262626", fontSize: 11 }}
                      labelStyle={{ color: "#8E8E93" }}
                      formatter={(v) => (g.metric === "avg_reaction_ms" ? `${Math.round(v)}ms` : v)}
                      labelFormatter={(l, p) => (p?.[0]?.payload?.date ? `Intento ${l} · ${p[0].payload.date}` : `Intento ${l}`)}
                    />
                    <Line type="monotone" dataKey="value" stroke={g.color} strokeWidth={2} dot={{ r: 3, fill: g.color }} isAnimationActive={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
